import { request } from './client'

/**
 * Turning device notifications on and off.
 *
 * <p>Three things have to line up before a push can arrive — the server has
 * VAPID keys, the browser supports push, and the person has granted permission
 * — and each fails differently. `pushStatus` reports which one is missing so a
 * screen can say something true rather than a generic "unavailable".
 *
 * <p>Permission is never requested on load. A browser shows that prompt once
 * and remembers "no" forever, so asking before the person has any idea what
 * they would be agreeing to spends the only chance there is.
 */
export type PushState =
  | 'unsupported' // this browser has no push
  | 'unconfigured' // the deployment has no VAPID keys
  | 'denied' // the person said no, and only they can undo it
  | 'off' // available, not enabled
  | 'on'

type KeyResponse = { publicKey: string; enabled: boolean }

/** VAPID keys travel base64url; `applicationServerKey` wants raw bytes. */
function toBytes(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  // An ArrayBuffer rather than the Uint8Array view: `applicationServerKey`
  // wants a BufferSource backed by a plain ArrayBuffer, and a Uint8Array's
  // buffer is typed loosely enough to include SharedArrayBuffer, which it will
  // not accept.
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes.buffer
}

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

async function existing(): Promise<PushSubscription | null> {
  if (!supported()) return null
  const registration = await navigator.serviceWorker.ready
  return registration.pushManager.getSubscription()
}

export async function pushStatus(): Promise<PushState> {
  if (!supported()) return 'unsupported'

  let key: KeyResponse
  try {
    key = await request<KeyResponse>('/me/push/key')
  } catch {
    return 'unconfigured'
  }
  if (!key.enabled || key.publicKey === '') return 'unconfigured'

  // Checked after the server, so a deployment without keys reads as
  // "unconfigured" rather than blaming the person for a "denied" they never
  // caused.
  if (Notification.permission === 'denied') return 'denied'

  return (await existing()) ? 'on' : 'off'
}

/**
 * Asks permission, subscribes the browser, and registers it with the server.
 *
 * @param surface which app is asking, so a client notification is not delivered
 *   to a provider's installed app
 * @returns the state afterwards — `denied` if the person refused, which is
 *   final until they change it in browser settings
 */
export async function enablePush(surface: 'client' | 'provider'): Promise<PushState> {
  if (!supported()) return 'unsupported'

  const key = await request<KeyResponse>('/me/push/key')
  if (!key.enabled || key.publicKey === '') return 'unconfigured'

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'

  const registration = await navigator.serviceWorker.ready
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required by every browser: a push that is not shown to the user is not
      // allowed, and declaring it up front is how that is enforced.
      userVisibleOnly: true,
      applicationServerKey: toBytes(key.publicKey),
    }))

  const json = subscription.toJSON()
  await request<void>('/me/push', {
    method: 'POST',
    body: {
      endpoint: subscription.endpoint,
      p256dh: json.keys?.['p256dh'] ?? '',
      auth: json.keys?.['auth'] ?? '',
      surface,
    },
  })
  return 'on'
}

/**
 * Turns it off on this device.
 *
 * <p>Unsubscribes the browser *and* tells the server, in that order. Doing only
 * the first would leave the server pushing to an endpoint that no longer
 * exists; only the second would leave the browser willing to wake for pushes
 * that never come.
 */
export async function disablePush(): Promise<PushState> {
  const subscription = await existing()
  if (!subscription) return 'off'

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  try {
    await request<void>('/me/push', { method: 'DELETE', body: { endpoint } })
  } catch {
    // The browser has already stopped listening, which is what the person
    // asked for. A stale row is tidied the next time a push to it 410s.
  }
  return 'off'
}
